import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import React from 'react';
import cn from 'classnames';
import { ChevronRight, Kebab } from '../../icons';
import { useDevice } from '../../providers/Device';
import { Button } from '../Button';
import { Divider } from '../Divider';
import { DropdownMenu } from '../DropdownMenu';
import { FullPageModalHeaderEnd } from '../FullPageModal';
import { IconButton } from '../IconButton';
import { Spinner } from '../Spinner';
import { TextButton } from '../TextButton';
import styles from './FullPageModalHeaderActions.module.scss';
import { forwardedBiProps, GetFullPageModalHeaderActionsTID, isHeaderActionItem } from './FullPageModalHeaderActions.utils';
const resolveDropdownSkin = (action)=>{
    var _action_dropdownSkin;
    return (_action_dropdownSkin = action.dropdownSkin) !== null && _action_dropdownSkin !== void 0 ? _action_dropdownSkin : action.skin === 'error' || action.skin === 'secondary' ? action.skin : undefined;
};
const renderInlineAdditional = (action, fallbackTestId)=>{
    const biForward = forwardedBiProps(action);
    return /*#__PURE__*/ _jsx(TextButton, _object_spread_props(_object_spread({}, biForward), {
        underline: "none",
        skin: action.skin || 'dark',
        weight: action.weight,
        intent: action.intent,
        disabled: action.disabled || action.loading,
        loading: action.loading,
        prefixIcon: action.iconStart,
        testId: action.testId || fallbackTestId,
        tooltip: action.tooltipTitle ? {
            title: action.tooltipTitle
        } : undefined,
        onClick: action.onClick,
        children: action.text
    }));
};
const renderDropdownItem = (action, fallbackTestId, isMobileDevice)=>{
    const biForward = forwardedBiProps(action);
    const resolvedTestId = action.testId || fallbackTestId;
    const iconStart = action.loading ? /*#__PURE__*/ _jsx(Spinner, {
        size: 16,
        testId: "".concat(resolvedTestId, "-spinner")
    }) : action.iconStart;
    const opensSubPage = isMobileDevice && action.opens === 'subPage';
    return /*#__PURE__*/ _createElement(DropdownMenu.Item, _object_spread_props(_object_spread({}, biForward), {
        key: action.text,
        skin: resolveDropdownSkin(action),
        intent: action.intent,
        disabled: action.disabled || action.loading,
        testId: resolvedTestId,
        iconStart: iconStart,
        iconEnd: opensSubPage ? /*#__PURE__*/ _jsx(ChevronRight, {
            size: 16,
            testId: "".concat(resolvedTestId, "-chevron")
        }) : undefined,
        iconEndPlacement: opensSubPage ? 'trailingEdge' : 'afterLabel',
        closeOnClick: !opensSubPage,
        onClick: action.onClick,
        tooltipProps: action.tooltipTitle ? {
            title: action.tooltipTitle
        } : undefined
    }), action.text);
};
const renderHeaderActionDesktop = (action, fallbackTestId, defaultSkin)=>{
    var _action_iconPosition;
    const biForward = forwardedBiProps(action);
    const isIconAtEnd = ((_action_iconPosition = action.iconPosition) !== null && _action_iconPosition !== void 0 ? _action_iconPosition : 'start') === 'end';
    return /*#__PURE__*/ _jsx(Button, _object_spread_props(_object_spread({}, biForward), {
        skin: action.skin || defaultSkin,
        intent: action.intent,
        disabled: action.disabled,
        loading: action.loading,
        type: action.type,
        form: action.form,
        testId: action.testId || fallbackTestId,
        tooltip: action.tooltipTitle ? {
            title: action.tooltipTitle
        } : undefined,
        onClick: action.onClick,
        "aria-label": action.text,
        prefixIcon: isIconAtEnd ? undefined : action.icon,
        suffixIcon: isIconAtEnd ? action.icon : undefined,
        children: action.text
    }));
};
const MOBILE_ICON_SIZE = 16;
const renderHeaderActionMobile = (action, fallbackTestId, defaultSkin)=>{
    var _action_loading;
    const biForward = forwardedBiProps(action);
    const resolvedTestId = action.testId || fallbackTestId;
    const icon = /*#__PURE__*/ React.cloneElement(action.icon, {
        size: MOBILE_ICON_SIZE
    });
    return /*#__PURE__*/ _jsx(IconButton, _object_spread_props(_object_spread({}, biForward), {
        skin: action.mobileSkin || defaultSkin,
        size: "smallMedium",
        intent: action.intent,
        disabled: action.disabled || action.loading,
        type: action.type,
        form: action.form,
        testId: resolvedTestId,
        tooltip: action.tooltipTitle ? {
            title: action.tooltipTitle
        } : undefined,
        onClick: action.onClick,
        "aria-label": action.text,
        "aria-busy": (_action_loading = action.loading) !== null && _action_loading !== void 0 ? _action_loading : false,
        children: action.loading ? /*#__PURE__*/ _jsx(Spinner, {
            intent: "inherit",
            size: MOBILE_ICON_SIZE,
            testId: "".concat(resolvedTestId, "-spinner")
        }) : icon
    }));
};
const renderSecondary = (action, isMobile, fallbackTestId)=>isMobile ? renderHeaderActionMobile(action, fallbackTestId, 'primaryTransparentBoxed') : renderHeaderActionDesktop(action, fallbackTestId, 'primaryOutline');
const renderMain = (main, isMobile, fallbackTestId)=>{
    if (main == null) return null;
    if (!isHeaderActionItem(main)) return main;
    return isMobile ? renderHeaderActionMobile(main, fallbackTestId, 'primaryBoxed') : renderHeaderActionDesktop(main, fallbackTestId, 'primary');
};
export function FullPageModalHeaderActions(props) {
    var _ref, _ref1, _ref2, _props_kebabAriaLabel;
    var _props_additionalActions, _props_additionalActions1, _props_additionalActions2;
    const { isMobileDevice } = useDevice();
    const { FullPageModalHeaderActionsTestIds } = React.useMemo(()=>GetFullPageModalHeaderActionsTID(props.testId), [
        props.testId
    ]);
    const inline = (_ref = (_props_additionalActions = props.additionalActions) === null || _props_additionalActions === void 0 ? void 0 : _props_additionalActions.inline) !== null && _ref !== void 0 ? _ref : [];
    const dropdown = (_ref1 = (_props_additionalActions1 = props.additionalActions) === null || _props_additionalActions1 === void 0 ? void 0 : _props_additionalActions1.dropdown) !== null && _ref1 !== void 0 ? _ref1 : [];
    const visibleInline = isMobileDevice ? [] : inline;
    const visibleDropdown = isMobileDevice ? [
        ...inline,
        ...dropdown
    ] : dropdown;
    const hasKebab = visibleDropdown.length > 0;
    const hasAdditional = visibleInline.length > 0 || hasKebab;
    const hasTrailingDivider = hasAdditional && ((_ref2 = (_props_additionalActions2 = props.additionalActions) === null || _props_additionalActions2 === void 0 ? void 0 : _props_additionalActions2.withTrailingDivider) !== null && _ref2 !== void 0 ? _ref2 : false) && (props.secondaryAction != null || props.mainAction != null);
    return /*#__PURE__*/ _jsx(FullPageModalHeaderEnd, {
        children: /*#__PURE__*/ _jsxs("div", {
            className: cn(styles.root, {
                [styles.mobile]: isMobileDevice
            }),
            "data-testid": FullPageModalHeaderActionsTestIds.Root(),
            children: [
                hasAdditional && /*#__PURE__*/ _jsxs("div", {
                    className: styles.additionalBlock,
                    "data-testid": FullPageModalHeaderActionsTestIds.AdditionalBlock(),
                    children: [
                        visibleInline.map((action, idx)=>/*#__PURE__*/ _jsxs(React.Fragment, {
                                children: [
                                    renderInlineAdditional(action, FullPageModalHeaderActionsTestIds.InlineAction(idx)),
                                    (idx < visibleInline.length - 1 || hasKebab) && /*#__PURE__*/ _jsx(Divider, {
                                        orientation: "vertical",
                                        margin: 12,
                                        size: 24
                                    })
                                ]
                            }, action.text)),
                        hasKebab && /*#__PURE__*/ _jsx(DropdownMenu, {
                            testId: FullPageModalHeaderActionsTestIds.DropdownMenu(),
                            vAlign: "bottom",
                            target: /*#__PURE__*/ _jsx(IconButton, {
                                // eslint-disable-next-line custom/no-hardcoded-bi-name-in-seagull -- the kebab is the component's own inherent overflow trigger, not a consumer-supplied action
                                bi: {
                                    name: 'more_actions'
                                },
                                skin: "transparent",
                                noPadding: true,
                                size: "small",
                                testId: FullPageModalHeaderActionsTestIds.KebabButton(),
                                "aria-label": (_props_kebabAriaLabel = props.kebabAriaLabel) !== null && _props_kebabAriaLabel !== void 0 ? _props_kebabAriaLabel : 'More actions',
                                children: /*#__PURE__*/ _jsx(Kebab, {})
                            }),
                            children: visibleDropdown.map((action, idx)=>renderDropdownItem(action, FullPageModalHeaderActionsTestIds.DropdownAction(idx), isMobileDevice))
                        })
                    ]
                }),
                hasTrailingDivider && /*#__PURE__*/ _jsx(Divider, {
                    orientation: "vertical",
                    margin: 12,
                    size: 24
                }),
                props.secondaryAction && renderSecondary(props.secondaryAction, isMobileDevice, FullPageModalHeaderActionsTestIds.SecondaryAction()),
                renderMain(props.mainAction, isMobileDevice, FullPageModalHeaderActionsTestIds.MainAction())
            ]
        })
    });
}
